import React from "react";

import { Skeleton } from "primereact/skeleton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Avatar } from "primereact/avatar";
import { useSelector } from "react-redux";

import FileIcon from "../../../../../components/file-type-icon";
import {
  downloadFileHandler,
  openPreviewInNewTab,
} from "../../../../../utils/fileUtils";
import "./attachments.scss";
import DocumentDescription from "~/components/document-description";
import { useParams } from "react-router-dom";

const AttachmentTable = ({
  formatDate,
  processAttachments,
  loadingAttachments,
  rows,
  onPageChange,
  first,
  showTypeColumn = true,
  handleAttachmentDelete,
  isPayslipFolder = false,
  relatedType = "",
  relatedId = "",
}) => {
  const { id: userId, role } = useSelector((state) => state.login);
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} Bytes`;
    if (bytes < 1048576) return `${Math.floor(bytes / 1024)} KB`;
    return `${Math.floor(bytes / 1048576)} MB`;
  };

  const getInitials = (fullName) => {
    const nameArray = fullName.split(" ");
    const firstInitial = nameArray[0]?.charAt(0) || "";
    const lastInitial =
      nameArray.length > 1 ? nameArray[nameArray.length - 1].charAt(0) : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <div className="attachments__body">
      {loadingAttachments ? (
        <DataTable
          value={[...Array(5)]}
          tableStyle={{ minWidth: "50rem" }}
          className="datatable-right-align"
        >
          <Column
            header="Name"
            body={() => <Skeleton width="100%" />}
            sortable
            className="table-col-width"
          />
          {isPayslipFolder && (
            <Column
              header="Category"
              body={() => <Skeleton />}
              className="table-col-width"
            />
          )}
          <Column
            header="Uploaded By"
            body={() => <Skeleton />}
            sortable
            className="table-col-width"
          />
          <Column
            header="Creation Date"
            body={() => <Skeleton />}
            className="table-col-width"
          />
          <Column
            header="File Size"
            body={() => <Skeleton />}
            className="table-col-width"
          />
          {showTypeColumn && !isPayslipFolder && (
            <Column
              header="Type"
              body={() => <Skeleton />}
              className="table-col-width"
            />
          )}
          <Column body={() => <Skeleton />} className="table-col-width" />
        </DataTable>
      ) : (
        <DataTable
          value={processAttachments}
          tableStyle={{ minWidth: "50rem" }}
          className="datatable-right-align"
          paginator={processAttachments?.length > 10}
          first={first}
          rows={rows}
          onPage={onPageChange}
          totalRecords={processAttachments?.length}
          rowsPerPageOptions={[10, 20, 30]}
          emptyMessage="No files found"
        >
          <Column
            header="Name"
            field="name"
            body={(field) => (
              <div className="file__name">
                <FileIcon
                  isPayslipFolder={isPayslipFolder}
                  filetype={
                    isPayslipFolder ? "application/pdf" : field?.file_type
                  }
                  filename={
                    isPayslipFolder ? field?.original_file_name : field?.name
                  }
                  downloadFile={() =>
                    downloadFileHandler({
                      blob_url: isPayslipFolder
                        ? field?.file_reference
                        : field?.blob_url,
                      name: isPayslipFolder
                        ? field?.extracted_data?.original_filename
                        : field?.name,
                    })
                  }
                />
              </div>
            )}
            sortable
          />
          {isPayslipFolder && (
            <Column
              header="Category"
              body={() => "Payslips"}
              className="table-col-width"
            />
          )}
          <Column
            header="Uploaded By"
            field="created_by"
            body={(field) => (
              <div className="uploaded__by">
                <Avatar
                  shape="circle"
                  label={
                    isPayslipFolder
                      ? getInitials(
                          [
                            field?.userData?.first_name,
                            field?.userData?.last_name,
                          ]
                            .filter(Boolean)
                            .join(" "),
                        )
                      : getInitials(
                          [
                            field?.createdByData?.first_name,
                            field?.createdByData?.last_name,
                          ]
                            .filter(Boolean)
                            .join(" "),
                        )
                  }
                />
                {isPayslipFolder
                  ? [field?.userData?.first_name, field?.userData?.last_name]
                      .filter(Boolean)
                      .join(" ")
                  : [
                      field?.createdByData?.first_name,
                      field?.createdByData?.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ")}
              </div>
            )}
            sortable
            className="table-col-width"
          />
          <Column
            header="Creation Date"
            body={(field) => formatDate(field?.createdAt)}
            className="table-col-width"
          />
          <Column
            header="File Size"
            body={(field) =>
              formatFileSize(
                isPayslipFolder ? field?.extracted_data?.fileSize : field?.size,
              )
            }
          />
          {showTypeColumn && !isPayslipFolder && (
            <Column
              header="Type"
              body={(field) =>
                field?.isPayslipData
                  ? "Payslips"
                  : field?.metadata?.is_public
                    ? "Public"
                    : "Internal"
              }
              className="table-col-width"
            />
          )}

          <Column
            body={(field) => (
              <>
                <div className="flex align-items-center">
                  <DocumentDescription
                    data={field}
                    id={relatedId}
                    type={relatedType}
                  />
                  <span
                    className="pi pi-download pencil-pointer"
                    onClick={() =>
                      downloadFileHandler({
                        blob_url: isPayslipFolder
                          ? field?.file_reference
                          : field?.blob_url,
                        name: isPayslipFolder
                          ? field?.extracted_data?.original_filename
                          : field?.name,
                      })
                    }
                  ></span>
                  {(role === "GXM_ADMIN" ||
                    (!isPayslipFolder && field.created_by === userId) ||
                    (field?.isPayslipData && role.includes(field.owner)) ||
                    (isPayslipFolder && field.user_id === userId)) && (
                    <span
                      className="pi pi-trash pencil-pointer"
                      onClick={() => handleAttachmentDelete(field)}
                    ></span>
                  )}
                </div>
              </>
            )}
          />
        </DataTable>
      )}
    </div>
  );
};

export default AttachmentTable;
